import apiClient, { unwrapResponse } from './apiClient';
import { API_ENDPOINTS } from './endpoints';
import {
  DailyPerformance,
  MissReason,
  UserBandwidth,
  TeamBandwidth,
  WorkBasedAttendance,
  LeaveRequest,
  LeaveImpactAnalysis,
  LeaveBalance,
  LeaveType,
  ApiResponse,
} from '@/types';

// ============================================================================
// PERFORMANCE SERVICE
// ============================================================================

export const performanceService = {
  /**
   * Get daily performance for current user
   */
  async getDailyPerformance(date?: string): Promise<ApiResponse<DailyPerformance>> {
    const response = await apiClient.get(API_ENDPOINTS.PERFORMANCE.DAILY, {
      params: { date },
    });
    return response.data;
  },

  /**
   * Get performance for a specific date
   */
  async getPerformanceByDate(date: string): Promise<ApiResponse<DailyPerformance>> {
    const response = await apiClient.get(API_ENDPOINTS.PERFORMANCE.GET_DATE(date));
    return response.data;
  },

  /**
   * Submit miss reason for a task. Backend requires date, taskId, reason; category and notes optional.
   */
  async submitMissReason(
    taskId: string,
    reason: Omit<MissReason, 'taskId'> & { date?: string }
  ): Promise<ApiResponse<void>> {
    const date = reason.date ?? new Date().toISOString().slice(0, 10);
    const { date: _d, ...rest } = reason;
    const response = await apiClient.post(
      API_ENDPOINTS.PERFORMANCE.SUBMIT_MISS_REASON,
      { taskId, date, ...rest }
    );
    return response.data;
  },

  /**
   * Get weekly performance summary
   */
  async getWeeklyPerformance(weekStart?: string): Promise<ApiResponse<DailyPerformance[]>> {
    const response = await apiClient.get(API_ENDPOINTS.PERFORMANCE.WEEKLY, {
      params: { weekStart },
    });
    return response.data;
  },

  /**
   * Get team performance stats
   */
  async getTeamPerformance(teamId: string): Promise<ApiResponse<any>> {
    const response = await apiClient.get(
      API_ENDPOINTS.PERFORMANCE.TEAM_STATS(teamId)
    );
    return response.data;
  },

  /**
   * Get pending reviews for current user. Returns unwrapped array.
   */
  async getPendingReviews(): Promise<any[]> {
    const response = await apiClient.get(API_ENDPOINTS.REVIEWS.MY_PENDING);
    const data = unwrapResponse(response);
    return Array.isArray(data) ? data : [];
  },
};

// ============================================================================
// BANDWIDTH SERVICE
// ============================================================================

export const bandwidthService = {
  /**
   * Get current user's bandwidth
   */
  async getMyBandwidth(date?: string): Promise<ApiResponse<UserBandwidth>> {
    const response = await apiClient.get(API_ENDPOINTS.BANDWIDTH.MY_BANDWIDTH, {
      params: { date },
    });
    return response.data;
  },

  /**
   * Get bandwidth for a specific user
   */
  async getUserBandwidth(
    userId: string,
    date?: string
  ): Promise<ApiResponse<UserBandwidth>> {
    const response = await apiClient.get(
      API_ENDPOINTS.BANDWIDTH.USER_BANDWIDTH(userId),
      { params: { date } }
    );
    return response.data;
  },

  /**
   * Get team bandwidth overview
   */
  async getTeamBandwidth(
    teamId: string,
    date?: string
  ): Promise<ApiResponse<TeamBandwidth>> {
    const response = await apiClient.get(
      API_ENDPOINTS.BANDWIDTH.TEAM_BANDWIDTH(teamId),
      { params: { date } }
    );
    return response.data;
  },

  /**
   * Check user availability before assignment
   */
  async checkAvailability(
    userId: string,
    hoursNeeded: number,
    startDate: string,
    endDate: string
  ): Promise<ApiResponse<{
    isAvailable: boolean;
    currentLoad: number;
    suggestedUsers?: string[];
  }>> {
    const response = await apiClient.post(
      API_ENDPOINTS.BANDWIDTH.CHECK_AVAILABILITY,
      { userId, hoursNeeded, startDate, endDate }
    );
    return response.data;
  },

  /**
   * Get bandwidth forecast for next week
   */
  async getForecast(): Promise<ApiResponse<UserBandwidth[]>> {
    const response = await apiClient.get(API_ENDPOINTS.BANDWIDTH.FORECAST);
    return response.data;
  },
};

// ============================================================================
// ATTENDANCE SERVICE
// ============================================================================

export const attendanceService = {
  /**
   * Get current user's attendance
   */
  async getMyAttendance(
    startDate?: string,
    endDate?: string
  ): Promise<ApiResponse<WorkBasedAttendance[]>> {
    const response = await apiClient.get(API_ENDPOINTS.ATTENDANCE.MY_ATTENDANCE, {
      params: { startDate, endDate },
    });
    return response.data;
  },

  /**
   * Get attendance for a specific user
   */
  async getUserAttendance(
    userId: string,
    startDate?: string,
    endDate?: string
  ): Promise<ApiResponse<WorkBasedAttendance[]>> {
    const response = await apiClient.get(
      API_ENDPOINTS.ATTENDANCE.USER_ATTENDANCE(userId),
      { params: { startDate, endDate } }
    );
    return response.data;
  },

  /**
   * Get team attendance
   */
  async getTeamAttendance(
    teamId: string,
    date?: string
  ): Promise<ApiResponse<WorkBasedAttendance[]>> {
    const response = await apiClient.get(
      API_ENDPOINTS.ATTENDANCE.TEAM_ATTENDANCE(teamId),
      { params: { date } }
    );
    return response.data;
  },

  /**
   * Override attendance (manager only)
   */
  async overrideAttendance(
    attendanceId: string,
    status: string,
    reason: string
  ): Promise<ApiResponse<WorkBasedAttendance>> {
    const response = await apiClient.patch(
      API_ENDPOINTS.ATTENDANCE.OVERRIDE(attendanceId),
      { status, reason }
    );
    return response.data;
  },
};

// ============================================================================
// LEAVE SERVICE
// ============================================================================

export const leaveService = {
  /**
   * Get my leave requests. Backend returns paginated { items, total, page, ... }; normalized to data array.
   */
  async getMyLeaveRequests(): Promise<ApiResponse<LeaveRequest[]>> {
    const response = await apiClient.get(API_ENDPOINTS.LEAVE.MY_REQUESTS);
    const envelope = response.data as { success?: boolean; data?: { items?: LeaveRequest[] }; message?: string };
    const items = envelope?.data?.items ?? [];
    return { ...envelope, data: items };
  },

  /**
   * Get a specific leave request
   */
  async getLeaveRequest(id: string): Promise<ApiResponse<LeaveRequest>> {
    const response = await apiClient.get(API_ENDPOINTS.LEAVE.GET(id));
    return response.data;
  },

  /**
   * Create a leave request
   */
  async createLeaveRequest(data: {
    type: LeaveType;
    startDate: string;
    endDate: string;
    reason: string;
    delegateTo?: string;
  }): Promise<ApiResponse<LeaveRequest>> {
    const payload = {
      ...data,
      startDate: data.startDate.slice(0, 10),
      endDate: data.endDate.slice(0, 10),
    };
    const response = await apiClient.post(API_ENDPOINTS.LEAVE.CREATE, payload);
    return response.data;
  },

  /**
   * Cancel a leave request
   */
  async cancelLeaveRequest(id: string): Promise<ApiResponse<void>> {
    const response = await apiClient.post(API_ENDPOINTS.LEAVE.CANCEL(id));
    return response.data;
  },

  /**
   * Withdraw a leave request (uses cancel endpoint; backend cancels pending requests).
   */
  async withdrawLeaveRequest(id: string): Promise<ApiResponse<LeaveRequest>> {
    const response = await apiClient.post(API_ENDPOINTS.LEAVE.CANCEL(id));
    return response.data;
  },

  /**
   * Get leave calendar for current user in date range.
   */
  async getLeaveCalendar(startDate: string, endDate: string): Promise<ApiResponse<LeaveRequest[]>> {
    const response = await apiClient.get(API_ENDPOINTS.LEAVE.CALENDAR, {
      params: { start: startDate.slice(0, 10), end: endDate.slice(0, 10) },
    });
    return response.data;
  },

  /**
   * Get impact analysis for leave dates
   */
  async getImpactAnalysis(
    startDate: string,
    endDate: string
  ): Promise<ApiResponse<LeaveImpactAnalysis>> {
    const response = await apiClient.post(API_ENDPOINTS.LEAVE.IMPACT_ANALYSIS, {
      startDate,
      endDate,
    });
    return response.data;
  },

  /**
   * Get leave balance
   */
  async getLeaveBalance(): Promise<ApiResponse<LeaveBalance>> {
    const response = await apiClient.get(API_ENDPOINTS.LEAVE.BALANCE);
    return response.data;
  },

  /**
   * Get leave balance for specific user
   */
  async getUserLeaveBalance(userId: string, year?: number): Promise<ApiResponse<LeaveBalance>> {
    const params = year ? { year } : {};
    const response = await apiClient.get(API_ENDPOINTS.LEAVE.USER_BALANCE(userId), { params });
    return response.data;
  },

  /**
   * Get leave requests for specific user
   */
  async getUserLeaveRequests(userId: string): Promise<ApiResponse<LeaveRequest[]>> {
    const response = await apiClient.get(API_ENDPOINTS.LEAVE.REQUESTS, {
      params: { userId, pageSize: 100 },
    });
    const envelope = response.data as { success?: boolean; data?: { items?: LeaveRequest[] }; message?: string };
    const items = envelope?.data?.items ?? [];
    return { ...envelope, data: items };
  },

  // Manager actions
  /**
   * Get pending leave approvals (manager)
   */
  async getPendingApprovals(): Promise<ApiResponse<LeaveRequest[]>> {
    const response = await apiClient.get(API_ENDPOINTS.LEAVE.PENDING_APPROVALS);
    return response.data;
  },

  /**
   * Approve leave request (manager)
   */
  async approveLeave(id: string, comments?: string): Promise<ApiResponse<LeaveRequest>> {
    const response = await apiClient.post(API_ENDPOINTS.LEAVE.APPROVE(id), {
      comments,
    });
    return response.data;
  },

  /**
   * Reject leave request (manager)
   */
  async rejectLeave(id: string, comments: string): Promise<ApiResponse<LeaveRequest>> {
    const response = await apiClient.post(API_ENDPOINTS.LEAVE.REJECT(id), {
      comments,
    });
    return response.data;
  },

  /**
   * Get leave calendar for team
   */
  async getTeamCalendar(teamId: string): Promise<ApiResponse<LeaveRequest[]>> {
    const response = await apiClient.get(
      API_ENDPOINTS.LEAVE.TEAM_CALENDAR(teamId)
    );
    return response.data;
  },
};

export {
  performanceService as default,
};
